import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Save } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';

function CompanySettings() {
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/settings').then((r) => setName(r.data.company_name || '')).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings', { company_name: name });
      toast.success('Company name updated');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4 max-w-lg">
      <div className="space-y-1.5">
        <Label>{t('company_name')}</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="company-name-input" />
      </div>
      <Button onClick={handleSave} disabled={saving} data-testid="save-company-btn">
        <Save className="h-4 w-4 mr-1.5" /> {saving ? 'Saving...' : t('save')}
      </Button>
    </div>
  );
}

function UserManagement() {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'staff' });
  const [saving, setSaving] = useState(false);

  const fetchUsers = () => api.get('/users').then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { fetchUsers(); }, []);

  const handleAdd = async () => {
    setSaving(true);
    try {
      await api.post('/auth/register', form);
      toast.success('User created');
      setOpen(false);
      setForm({ name: '', email: '', password: '', role: 'staff' });
      fetchUsers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">{t('user_management')}</h3>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="add-user-btn">
          <Plus className="h-3.5 w-3.5 mr-1.5" /> {t('add_user')}
        </Button>
      </div>
      <div className="rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('name')}</TableHead>
              <TableHead>{t('email')}</TableHead>
              <TableHead>{t('role')}</TableHead>
              <TableHead className="w-20">{t('actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>
                  <Badge variant="secondary">{u.role}</Badge>
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(u.id)} data-testid={`delete-user-${u.email}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('add_user')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{t('name')}</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="new-user-name" />
            </div>
            <div className="space-y-1.5">
              <Label>{t('email')}</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="new-user-email" />
            </div>
            <div className="space-y-1.5">
              <Label>{t('password')}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="new-user-password" />
            </div>
            <div className="space-y-1.5">
              <Label>{t('role')}</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">{t('admin')}</SelectItem>
                  <SelectItem value="staff">{t('staff')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t('cancel')}</Button>
            <Button onClick={handleAdd} disabled={saving} data-testid="confirm-add-user">
              {saving ? 'Creating...' : t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CategoryManagement() {
  const { t } = useLanguage();
  const [cats, setCats] = useState([]);
  const [newCat, setNewCat] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');

  const fetchCats = () => api.get('/categories').then((r) => setCats(r.data)).catch(() => {});
  useEffect(() => { fetchCats(); }, []);

  const handleAdd = async () => {
    if (!newCat.trim()) return;
    try {
      await api.post('/categories', { name: newCat.trim() });
      toast.success('Category added');
      setNewCat('');
      fetchCats();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleUpdate = async (catId) => {
    if (!editName.trim()) return;
    try {
      await api.put(`/categories/${catId}`, { name: editName.trim() });
      toast.success('Category updated');
      setEditId(null);
      fetchCats();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleDelete = async (catId) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await api.delete(`/categories/${catId}`);
      toast.success('Category deleted');
      fetchCats();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4 max-w-lg">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">{t('category_management')}</h3>
      <div className="flex gap-2">
        <Input
          placeholder="New category name"
          value={newCat}
          onChange={(e) => setNewCat(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          data-testid="new-category-input"
        />
        <Button onClick={handleAdd} data-testid="add-category-btn">
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <div className="space-y-1">
        {cats.map((c) => (
          <div key={c.id} className="flex items-center gap-2 bg-muted/40 rounded-lg px-3 py-2">
            {editId === c.id ? (
              <>
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="h-8 flex-1"
                  autoFocus
                />
                <Button size="sm" variant="ghost" onClick={() => handleUpdate(c.id)}>
                  <Save className="h-3.5 w-3.5" />
                </Button>
              </>
            ) : (
              <>
                <span className="flex-1 text-sm">{c.name}</span>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => { setEditId(c.id); setEditName(c.name); }}
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => handleDelete(c.id)} data-testid={`delete-cat-${c.name}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  return (
    <div className="space-y-6" data-testid="settings-page">
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
        {t('settings')}
      </h1>

      <Tabs defaultValue="company" className="space-y-4">
        <TabsList>
          <TabsTrigger value="company" data-testid="tab-company">{t('company_settings')}</TabsTrigger>
          {isAdmin && <TabsTrigger value="users" data-testid="tab-users">{t('user_management')}</TabsTrigger>}
          {isAdmin && <TabsTrigger value="categories" data-testid="tab-categories">{t('category_management')}</TabsTrigger>}
        </TabsList>

        <TabsContent value="company">
          <Card>
            <CardContent className="p-5">
              <CompanySettings />
            </CardContent>
          </Card>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="users">
            <Card>
              <CardContent className="p-5">
                <UserManagement />
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="categories">
            <Card>
              <CardContent className="p-5">
                <CategoryManagement />
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
